import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, BookOpen, Layers, Monitor, Award } from 'lucide-react';

export default function Messesystemer() {
  const catalogUrl = "https://www.epaper.dk/public/neutral/dinudstilling_neutral_3/";

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden bg-white py-20 sm:py-24 lg:py-28 min-h-[350px] flex items-center border-b border-gray-100">
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-slate-50 via-slate-100/50 to-white" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8 w-full">
          <div className="max-w-3xl text-left">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-x-2 rounded-full bg-orange-50 px-3 py-1 text-sm font-semibold leading-6 text-orange-600 ring-1 ring-inset ring-orange-600/10 mb-4"
            >
              <BookOpen className="h-4 w-4" />
              <span>Elektronisk Katalog</span>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl font-bold tracking-tight text-[#1e335e] sm:text-7xl uppercase"
            >
              Messesystemer
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-xl leading-8 text-gray-900 font-medium"
            >
              Udforsk vores store udvalg af professionelle messesystemer, roll-ups, udstillingsvægge og tilbehør direkte i vores interaktive e-katalog.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-y-12 lg:grid-cols-3 lg:gap-x-12 mb-12">
          {/* Info Cards */}
          <div className="bg-slate-50 p-8 border border-slate-100 flex flex-col justify-between">
            <div>
              <div className="flex h-12 w-12 items-center justify-center bg-orange-500 text-white mb-6">
                <Layers className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1e335e] uppercase tracking-wider mb-3">Komplette Løsninger</h3>
              <p className="text-gray-600 leading-relaxed">
                Vi tilbyder alt fra simple roll-ups til store, modulære messevægge, diske og brochureholdere, der sikrer dig maksimal synlighed.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 p-8 border border-slate-100 flex flex-col justify-between">
            <div>
              <div className="flex h-12 w-12 items-center justify-center bg-orange-500 text-white mb-6">
                <Monitor className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1e335e] uppercase tracking-wider mb-3">Interaktivt Katalog</h3>
              <p className="text-gray-600 leading-relaxed">
                Bladr nemt igennem vores elektroniske katalog her på siden for at finde inspiration og se de mange muligheder og specifikationer.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 p-8 border border-slate-100 flex flex-col justify-between">
            <div>
              <div className="flex h-12 w-12 items-center justify-center bg-orange-500 text-white mb-6">
                <Award className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-[#1e335e] uppercase tracking-wider mb-3">Høj Kvalitet</h3>
              <p className="text-gray-600 leading-relaxed">
                Vores udstillingssystemer er designet til at blive brugt igen og igen. De er nemme at transportere, hurtige at sætte op og i absolut topkvalitet.
              </p>
            </div>
          </div>
        </div>

        {/* Catalog Embed Section */}
        <div className="mt-16">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-[#1e335e] uppercase">Gennemse Kataloget</h2>
              <p className="text-sm text-gray-500 mt-1">Brug knapperne i bunden af kataloget til at zoome, bladre eller gå i fuldskærm.</p>
            </div>
            <a
              href={catalogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-x-2 text-sm font-bold uppercase tracking-wider text-white bg-orange-500 px-6 py-3 hover:bg-orange-600 transition-all self-start sm:self-center"
            >
              <span>Åbn i nyt vindue</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          {/* Responsive Iframe Container */}
          <div className="relative w-full bg-slate-100 border border-gray-200 shadow-sm overflow-hidden" style={{ height: '750px' }}>
            <iframe
              src={catalogUrl}
              title="Messesystemer Elektronisk Katalog"
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
            />
          </div>

          {/* Contact CTA */}
          <div className="mt-16 bg-[#1e335e] text-white p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="relative z-10 max-w-2xl mx-auto">
              <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider mb-4">Fandt du det du søgte?</h3>
              <p className="text-slate-200 mb-8 leading-relaxed">
                Har du spørgsmål til specifikke messesystemer, priser, leveringstider eller ønsker du rådgivning til dit næste event? Vi sidder klar til at hjælpe dig.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="/kontakt"
                  className="inline-flex items-center justify-center text-sm font-bold uppercase tracking-wider text-[#1e335e] bg-white px-8 py-3.5 hover:bg-slate-100 transition-all"
                >
                  Kontakt os nu
                </a>
                <a
                  href="tel:+4551821153"
                  className="inline-flex items-center justify-center text-sm font-bold uppercase tracking-wider text-white border border-white/30 hover:border-white px-8 py-3.5 transition-all"
                >
                  Ring +45 5182 1153
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
